/**
 * Mutation-style hook for dumping the latest medical PA form JSON onto the
 * submission row. Exposes a callable trigger so callers can invoke the POST
 * imperatively (e.g. on save/continue).
 * @module useDumpLatestMedicalPaFormData
 */
import { useCallback, useRef, useState } from "react";
import {
  dumpLatestMedicalPaFormData,
  DumpLatestMedicalPaFormDataRequest,
} from "../api/postCall/dumpLatestMedicalPaFormData";
import { logError } from "../utils/customLogger";

/** State and trigger returned by {@link useDumpLatestMedicalPaFormData}. */
export interface UseDumpLatestMedicalPaFormDataReturn {
  /**
   * Sends the POST request. Resolves with the parsed response on success, or
   * `null` when the request fails (the error is captured in `error`).
   */
  dumpFormData: (
    payload: DumpLatestMedicalPaFormDataRequest,
  ) => Promise<unknown | null>;
  /** `true` while a request is in flight. */
  loading: boolean;
  /** Error message from the most recent failed request, otherwise `null`. */
  error: string | null;
  /** Parsed response from the most recent successful request. */
  data: unknown | null;
}

/**
 * Provides an imperative trigger to persist the latest medical PA form JSON.
 *
 * Any in-flight request is aborted when a new one starts, so rapid successive
 * saves do not race each other.
 *
 * @returns The `dumpFormData` trigger along with loading, error, and data state.
 */
const useDumpLatestMedicalPaFormData =
  (): UseDumpLatestMedicalPaFormDataReturn => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<unknown | null>(null);
    const controllerRef = useRef<AbortController | null>(null);

    const dumpFormData = useCallback(
      async (
        payload: DumpLatestMedicalPaFormDataRequest,
      ): Promise<unknown | null> => {
        controllerRef.current?.abort();
        const controller = new AbortController();
        controllerRef.current = controller;

        setLoading(true);
        setError(null);

        try {
          const response = await dumpLatestMedicalPaFormData(
            payload,
            controller.signal,
          );
          setData(response);
          return response;
        } catch (err) {
          if ((err as Error).name === "AbortError") {
            return null;
          }
          logError(err as Error, "Failed to dump latest medical PA form data");
          setError((err as Error).message);
          return null;
        } finally {
          if (controllerRef.current === controller) {
            setLoading(false);
          }
        }
      },
      [],
    );

    return { dumpFormData, loading, error, data };
  };

export default useDumpLatestMedicalPaFormData;
